import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { parentRoute } from '@/lib/navigation';

/**
 * Bottom-of-page navigation row — "← Back" pill on the left,
 * "↑ Top" round icon button on the right. Visual match with the
 * header back-button. Drop this just before the closing `</main>`
 * on any long-form screen (Diary, Profile, Rituals, Weekly, etc.).
 *
 * Props:
 *   backLabel  — text shown next to the arrow. Defaults to the
 *                localized "Back".
 *   onBack     — custom back handler. Defaults to parent-aware
 *                navigation (see lib/navigation.ts).
 */
interface Props {
  backLabel?: string;
  onBack?: () => void;
}

export default function PageNav({ backLabel, onBack }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const goBack = () => {
    if (onBack) return onBack();
    // Parent-aware navigation — see Header.tsx for the same logic.
    navigate(parentRoute(location.pathname));
  };

  return (
    <div className="mt-8 flex items-center justify-between gap-3">
      <button type="button" onClick={goBack} className="nav-back-pill">
        <span>←</span>
        <span>{backLabel ?? t('nav.back')}</span>
      </button>
      <button
        type="button"
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        aria-label={t('nav.to_top')}
        className="nav-scroll-top"
      >
        ↑
      </button>
    </div>
  );
}
